import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiZap, FiCamera, FiHome, FiSun, FiShield, FiBattery, FiWifi,
  FiTool, FiFileText, FiSettings, FiCheck, FiX, FiStar, FiAward, FiPhone
} = FiIcons;

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);

  const services = [
    { id: 1, icon: FiZap, title: 'Electrical Installation & Maintenance', shortDesc: 'Complete electrical wiring, repairs, and maintenance.', fullDesc: 'Professional electrical installation services including complete home wiring, circuit installation, and maintenance.', features: ['Complete Home Wiring', 'Safety Inspections'], color: 'from-yellow-500 to-orange-500', bgColor: 'from-yellow-500/10 to-orange-500/10' },
    { id: 2, icon: FiZap, title: 'Electrician Services', shortDesc: 'Expert electrician services for all repairs.', fullDesc: 'Licensed electrician services covering troubleshooting and emergency repairs.', features: ['Emergency Repairs', 'Fault Diagnosis'], color: 'from-blue-500 to-indigo-500', bgColor: 'from-blue-500/10 to-indigo-500/10' },
    { id: 3, icon: FiCamera, title: 'CCTV Installation & Repairing', shortDesc: 'Professional CCTV camera installation.', fullDesc: 'Complete CCTV surveillance solutions including IP cameras and remote monitoring.', features: ['HD Cameras', 'Remote Monitoring'], color: 'from-purple-500 to-pink-500', bgColor: 'from-purple-500/10 to-pink-500/10' },
    { id: 4, icon: FiHome, title: 'Smart Home Automation', shortDesc: 'Transform your home with automation.', fullDesc: 'Complete smart home automation solutions including lighting and climate control.', features: ['Smart Lighting', 'Voice Control'], color: 'from-green-500 to-teal-500', bgColor: 'from-green-500/10 to-teal-500/10' },
    { id: 5, icon: FiSun, title: 'Solar Power Systems', shortDesc: 'Eco-friendly solar installations.', fullDesc: 'Complete solar power solutions including panel installation and battery storage.', features: ['Solar Panels', 'Battery Storage'], color: 'from-yellow-400 to-orange-400', bgColor: 'from-yellow-400/10 to-orange-400/10' },
    { id: 6, icon: FiBattery, title: 'Inverter & UPS Services', shortDesc: 'Reliable power backup solutions.', fullDesc: 'Complete power backup solutions including inverter and UPS installation.', features: ['Inverter Installation', 'UPS Systems'], color: 'from-red-500 to-pink-500', bgColor: 'from-red-500/10 to-pink-500/10' },
    { id: 7, icon: FiShield, title: 'Access Control & Security Systems', shortDesc: 'Advanced security for enhanced protection.', fullDesc: 'Comprehensive security solutions including biometric access control.', features: ['Biometric Systems', 'Alarm Systems'], color: 'from-indigo-500 to-purple-500', bgColor: 'from-indigo-500/10 to-purple-500/10' },
    { id: 8, icon: FiWifi, title: 'Networking & Structured Cabling', shortDesc: 'Professional network setup solutions.', fullDesc: 'Complete networking solutions including WiFi setup and structured cabling.', features: ['WiFi Setup', 'Network Security'], color: 'from-cyan-500 to-blue-500', bgColor: 'from-cyan-500/10 to-blue-500/10' },
    { id: 9, icon: FiTool, title: 'Electrical Appliance Repair', shortDesc: 'Expert repair services for appliances.', fullDesc: 'Professional appliance repair services for all electrical equipment.', features: ['Quick Repairs', 'Genuine Parts'], color: 'from-orange-500 to-red-500', bgColor: 'from-orange-500/10 to-red-500/10' },
    { id: 10, icon: FiFileText, title: 'Annual Maintenance Contracts (AMC)', shortDesc: 'Comprehensive maintenance contracts.', fullDesc: 'Complete annual maintenance packages for all systems.', features: ['Regular Inspections', 'Priority Service'], color: 'from-teal-500 to-green-500', bgColor: 'from-teal-500/10 to-green-500/10' },
    { id: 11, icon: FiSettings, title: 'Custom Electrical & Automation', shortDesc: 'Tailored solutions for your requirements.', fullDesc: 'Custom-designed electrical and automation solutions tailored for you.', features: ['Custom Design', 'Industrial Solutions'], color: 'from-purple-600 to-indigo-600', bgColor: 'from-purple-600/10 to-indigo-600/10' }
  ];

  const carePlans = [
    {
      id: 'basic',
      name: 'Basic Plan',
      price: '999',
      icon: FiZap,
      color: 'from-gray-600 to-gray-700',
      borderColor: 'border-gray-500/40',
      accentColor: 'text-yellow-400',
      badgeColor: 'bg-gray-700/60',
      popular: false,
      features: [
        { text: '4 Free Visits / Year', included: true },
        { text: 'After 5 Visits – Emergency Visits Charged', included: true, note: true },
        { text: 'Annual Inspection', included: true },
        { text: '5% Discount on Repairs', included: true },
        { text: 'Monthly Inspections', included: false },
        { text: '24/7 Emergency Support Hotline', included: false },
        { text: 'Dedicated Technician On Call', included: false },
      ]
    },
    {
      id: 'premium',
      name: 'Premium Plan',
      price: '2999',
      icon: FiAward,
      color: 'from-yellow-500 to-amber-600',
      borderColor: 'border-yellow-500/60',
      accentColor: 'text-yellow-400',
      badgeColor: 'bg-yellow-500/20',
      popular: true,
      features: [
        { text: '12 Free Visits / Year', included: true },
        { text: 'Monthly Inspections', included: true },
        { text: '24/7 Emergency Support Hotline', included: true },
        { text: 'Dedicated Technician On Call', included: true },
        { text: 'Complimentary Yearly Training Session', included: true, sub: 'Staff/Students – Security Device Usage' },
        { text: 'Exclusive WhatsApp Priority Support Group', included: true },
        { text: 'Free Basic Tool Kit', included: true, sub: 'Screwdriver, Pliers, Wire Crimper, Tester' },
        { text: 'VIP Loyalty Program: Upgrades & Multi-year Discounts', included: true },
      ]
    },
    {
      id: 'standard',
      name: 'Standard Plan',
      price: '1999',
      icon: FiStar,
      color: 'from-blue-500 to-cyan-600',
      borderColor: 'border-blue-500/50',
      accentColor: 'text-blue-400',
      badgeColor: 'bg-blue-500/20',
      popular: false,
      features: [
        { text: '8 Free Visits / Year', included: true },
        { text: 'After 8 Visits – Emergency Visits Charged', included: true, note: true },
        { text: 'Quarterly Inspections', included: true },
        { text: 'Free Minor Repairs (Switches, Sockets, Wiring Checks)', included: true },
        { text: 'Remote Troubleshooting for Smart Devices', included: true },
        { text: 'Referral Bonus: 1 Free Month Per Referral', included: true },
        { text: '10% Discount on Repairs', included: true },
      ]
    }
  ];

  const handleBookService = (serviceName) => {
    const whatsappMessage = `Hi! I'd like to book ${serviceName}. Please provide more details.`;
    window.open(`https://wa.me/919906669918?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
  };

  const handleBookPlan = (planName, price) => {
    const whatsappMessage = `Hi! I'm interested in the Tayyab Care Plan – ${planName} (₹${price}/year). Please provide more details.`;
    window.open(`https://wa.me/919906669918?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
  };

  return (
    <div className="min-h-screen pt-20">

      {/* ── Hero ── */}
      <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-blue-900/20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-40 h-40 bg-blue-500/30 rounded-full blur-xl animate-pulse" />
          <div className="absolute bottom-20 right-20 w-32 h-32 bg-cyan-500/30 rounded-full blur-xl animate-pulse" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <div className="inline-flex items-center space-x-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-2 text-sm text-blue-300">
            <SafeIcon icon={FiZap} className="text-lg" />
            <span>Complete Service Portfolio</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold">
            <span className="gradient-text">Professional Services</span><br />
            <span className="text-white">For Every Need</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            From basic electrical work to advanced smart home automation, we provide comprehensive solutions
            with professional expertise across Kashmir.
          </p>
        </div>
      </section>

      {/* ── Services Grid ── */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-blue-500/50 transition-all duration-500 cursor-pointer"
              onClick={() => setSelectedService(service)}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${service.bgColor} opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-500`} />
              <div className="relative mb-4">
                <div className={`w-14 h-14 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                  <SafeIcon icon={service.icon} className="text-xl text-white" />
                </div>
              </div>
              <div className="relative">
                <h3 className="text-lg font-bold text-white mb-3 group-hover:text-blue-300 transition-colors duration-300">{service.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">{service.shortDesc}</p>
                <button
                  onClick={(e) => { e.stopPropagation(); handleBookService(service.title); }}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-2 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-cyan-700 transition-all duration-300"
                >
                  Book This Service
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Tayyab Care Plan Section ── */}
      <section className="py-24 bg-gradient-to-b from-black via-[#020b1a] to-black relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[1px] bg-gradient-to-r from-transparent via-blue-500/40 to-transparent" />
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-600/5 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-64 h-64 bg-yellow-500/5 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500/3 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Logo + Header */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            {/* SVG Logo — extracted from flyer */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                {/* Outer glow ring */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-600/30 to-cyan-500/20 blur-xl scale-110" />
                <div className="relative bg-[#0a1628] border border-blue-500/30 rounded-2xl px-8 py-6 flex flex-col items-center shadow-2xl">
                  {/* Icon row */}
                  <div className="flex items-center space-x-3 mb-3">
                    {/* Electric plug icon */}
                    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                      <path d="M10 6v6M10 12c0 4 4 6 8 6s8-2 8-6V6" stroke="#0ea5e9" strokeWidth="2.5" strokeLinecap="round"/>
                      <path d="M18 18v4" stroke="#0ea5e9" strokeWidth="2.5" strokeLinecap="round"/>
                      <circle cx="18" cy="24" r="2" fill="#facc15"/>
                      <path d="M7 6h6M23 6h6" stroke="#0ea5e9" strokeWidth="2.5" strokeLinecap="round"/>
                    </svg>
                    {/* Heartbeat / waveform */}
                    <svg width="60" height="30" viewBox="0 0 60 30" fill="none">
                      <polyline points="0,15 10,15 15,5 20,25 25,10 30,20 35,15 60,15" stroke="#0ea5e9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {/* CCTV camera icon */}
                    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                      <rect x="4" y="10" width="22" height="16" rx="3" stroke="#0ea5e9" strokeWidth="2.5"/>
                      <polygon points="26,14 32,10 32,26 26,22" fill="#0ea5e9"/>
                      <circle cx="13" cy="18" r="3" fill="#facc15"/>
                    </svg>
                  </div>
                  {/* Brand name */}
                  <div className="text-center">
                    <span className="block text-3xl font-black tracking-[0.15em] text-white" style={{ fontFamily: 'Georgia, serif', letterSpacing: '0.18em' }}>TAYYAB</span>
                    <span className="block text-xs font-bold tracking-[0.3em] text-blue-300 mt-0.5">ELECTRIC STUDIO</span>
                    <span className="block text-[10px] tracking-[0.2em] text-gray-400 mt-1">POWERING HOMES, SECURING FUTURES.</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Care Plan badge + heading */}
            <div className="inline-flex items-center space-x-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full px-5 py-2 text-sm text-yellow-300 mb-6">
              <SafeIcon icon={FiShield} className="text-lg" />
              <span className="font-semibold tracking-wider uppercase text-xs">Tayyab Care Plan</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="gradient-text">Power. Security.</span>{' '}
              <span className="text-white">Comfort.</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              Choose the annual maintenance plan that fits your needs. All plans billed annually.
            </p>
          </motion.div>

          {/* Plans Grid — Premium in center / highlighted */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            {carePlans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                viewport={{ once: true }}
                className={`relative rounded-3xl border ${plan.borderColor} backdrop-blur-sm overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl ${
                  plan.popular
                    ? 'bg-gradient-to-b from-[#0d1f3c] to-[#071020] shadow-xl shadow-yellow-500/10 md:-mt-4'
                    : 'bg-gradient-to-b from-gray-900/80 to-gray-950/80'
                }`}
              >
                {/* Popular badge */}
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-yellow-500 to-amber-500 text-black text-xs font-black text-center py-1.5 tracking-widest uppercase">
                    ⭐ Most Popular
                  </div>
                )}

                <div className={`p-8 ${plan.popular ? 'pt-10' : ''}`}>
                  {/* Plan icon + name */}
                  <div className="flex items-center space-x-3 mb-6">
                    <div className={`w-12 h-12 bg-gradient-to-br ${plan.color} rounded-xl flex items-center justify-center shadow-lg`}>
                      <SafeIcon icon={plan.icon} className="text-xl text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                      <span className={`text-xs font-semibold ${plan.accentColor} uppercase tracking-wider`}>Annual Plan</span>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-8">
                    <div className="flex items-end space-x-1">
                      <span className={`text-5xl font-black ${plan.accentColor}`}>₹{plan.price}</span>
                      <span className="text-gray-400 mb-2 text-lg">/year</span>
                    </div>
                    <p className="text-gray-500 text-xs mt-1">All taxes included</p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start space-x-3">
                        <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${feature.included ? (feature.note ? 'bg-orange-500/20' : 'bg-green-500/20') : 'bg-gray-700/50'}`}>
                          {feature.included
                            ? <SafeIcon icon={FiCheck} className={`text-xs ${feature.note ? 'text-orange-400' : 'text-green-400'}`} />
                            : <SafeIcon icon={FiX} className="text-xs text-gray-600" />
                          }
                        </div>
                        <div>
                          <span className={`text-sm ${feature.included ? 'text-gray-200' : 'text-gray-600 line-through'}`}>
                            {feature.text}
                          </span>
                          {feature.sub && (
                            <p className="text-xs text-gray-500 mt-0.5">{feature.sub}</p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <button
                    onClick={() => handleBookPlan(plan.name, plan.price)}
                    className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-300 flex items-center justify-center space-x-2 ${
                      plan.popular
                        ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-black hover:from-yellow-400 hover:to-amber-400 shadow-lg shadow-yellow-500/25'
                        : `bg-gradient-to-r ${plan.color} text-white hover:opacity-90`
                    }`}
                  >
                    <SafeIcon icon={FiPhone} className="text-base" />
                    <span>Get This Plan</span>
                  </button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom note */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center text-gray-500 text-sm mt-10"
          >
            All plans are billed annually. · Call{' '}
            <a href="tel:+919906669918" className="text-blue-400 hover:underline">+91 99066 69918</a>
            {' '}for custom requirements.
          </motion.p>
        </div>
      </section>

      {/* ── Service Detail Modal ── */}
      {selectedService && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700"
          >
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className={`w-16 h-16 bg-gradient-to-br ${selectedService.color} rounded-xl flex items-center justify-center`}>
                  <SafeIcon icon={selectedService.icon} className="text-2xl text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">{selectedService.title}</h2>
                  <p className="text-blue-400">Professional Service</p>
                </div>
              </div>
              <button onClick={() => setSelectedService(null)} className="text-gray-400 hover:text-white transition-colors">
                <SafeIcon icon={FiX} className="text-2xl" />
              </button>
            </div>
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-white mb-3">Service Description</h3>
              <p className="text-gray-300 leading-relaxed">{selectedService.fullDesc}</p>
            </div>
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-white mb-4">What's Included</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {selectedService.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheck} className="text-green-400 text-sm" />
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => handleBookService(selectedService.title)}
                className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300"
              >
                Book This Service
              </button>
              <a
                href="tel:+919906669918"
                className="flex-1 border-2 border-blue-500 text-blue-400 py-3 px-6 rounded-lg font-semibold text-center hover:bg-blue-500 hover:text-white transition-all duration-300"
              >
                Call Now
              </a>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Services;